import React, { useCallback, useEffect, useState } from "react";
import styles from "../styles.module.scss";
import statsStyles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { Loading } from "app/components/Wrappers";
import ChartSection from "./ChartSection";
import { formatString } from "app/utils/strings";
import ParMetricsChart from "./ParMetricsChart";
import GirAccuracy from "./GirAccuracy";
import FairwayChart from "./FairwayChart";
import PuttingChart from "./PuttingChart";
import { FilterState } from "../ScoresFilterModal/utils";

interface Props {
  isModalOpen: boolean;
  athlete: any;
  filters: FilterState;
}

const Stats = ({ isModalOpen, athlete, filters }: Props) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const getSectionRoundLabel = (rounds: number): string => {
    if (rounds === 1) {
      return t('Based on 1 Round');
    }

    return formatString(t('Based on %{rounds} Rounds'), {
      rounds: String(rounds),
    });
  };

  const renderStats = () => {
    if (loading && (!stats || stats?.totalSummaryRounds === 0 || stats?.totalSummaryRounds === undefined)) {
      return <Loading isLoading loadType="spinner" />;
    }

    if ((!stats || stats?.totalSummaryRounds === 0 || stats?.totalSummaryRounds === undefined) && !loading) {
      return (
        <div className={styles.no_scores_container}>
          <i className='ico ico-analytics' />
          <p className={styles.no_events_title}>{t("No stats posted")}</p>
          <p className={styles.no_events_subtitle}>{t("All statistics will appear here.")}</p>
        </div>
      );
    }

    return (
      <div className={statsStyles.charts_container}>
        <div className={statsStyles.row}>
          <ChartSection
            leftText={t('Scoring Summary')}
            rightText={getSectionRoundLabel(stats.totalSummaryRounds || 0)}
          >
            <ParMetricsChart data={stats} />
          </ChartSection>
          <ChartSection
            leftText={t('Driving Accuracy')}
            rightText={getSectionRoundLabel(stats.totalStatsRounds)}>
            <FairwayChart data={stats.advancedStats} />
          </ChartSection>
        </div>
        <div className={statsStyles.row}>
          {stats.totalStatsRounds !== 0 &&(
            <ChartSection
              leftText={t('Approach')}
              rightText={getSectionRoundLabel(stats.totalStatsRounds)}>
              <GirAccuracy data={stats.advancedStats} />
            </ChartSection>
          )}
          <ChartSection
            leftText={t('Putting Summary')}
            rightText={getSectionRoundLabel(stats.totalStatsRounds)}>
            <PuttingChart data={stats.advancedStats} />
          </ChartSection>
        </div>
      </div>
    );
  };

  const fetchStats = useCallback(async () => {
    if (loading || !isModalOpen) { return; }

    setLoading(true);

    schedulingFetchGet(`/student_profiles/${athlete.externalId}/statistics`, {
      filter: filters.option,
      courseId: filters.course,
      teeId: filters.tee,
    })
      .then((response) => {
        setStats(response);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [athlete, loading, filters, isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) { return; }

    setStats(null);
    fetchStats();
  }, [isModalOpen, filters]);

  return (
    <>
      <div className={styles.metadata}>
        {t('stats_summary', {
          handicap_index: athlete?.handicapIndexDisplay || '-',
          low_hi: athlete?.lowHandicapIndexDisplay || '-',
        })}
      </div>

      {renderStats()}
    </>
  );
};

export default Stats;
